import {LeanDocument, Types} from 'mongoose';
import {API} from './api.class';
import {CreateCourseRequestDto} from '@/classes/dto/course-manager/request/CreateCourse.request.dto';
import {FindCourseRequestDto} from '@/classes/dto/course-manager/request/FindCourse.request.dto';
import {FindByIdOptionsRequestDto} from '@/classes/dto/_common/request/FindByIdOptions.request.dto';
import {UpdateCourseRequestDto} from '@/classes/dto/course-manager/request/UpdateCourse.request.dto';
import {DeleteCourseRequestDto} from '@/classes/dto/course-manager/request/DeleteCourse.request.dto';
import {AddStudentToCourseRequestDto} from '@/classes/dto/course-manager/request/AddStudentToCourse.request.dto';
import {
    RemoveStudentFromCourseRequestDto,
} from '@/classes/dto/course-manager/request/RemoveStudentFromCourse.request.dto';
import {TerminateCourseRequestDto} from '@/classes/dto/course-manager/request/TerminateCourse.request.dto';
import {PreviewCourseMailsRequestDto} from '@/classes/dto/course-manager/request/PreviewCourseMails.request.dto';
import {SendReviewedCourseMailsRequestDto} from '@/classes/dto/course-manager/request/SendReviewedCourseMails.request.dto';
import {PreviewCourseNotificationMailsRequestDto} from '@/classes/dto/course-manager/request/PreviewCourseNotificationMails.request.dto';
import {SendReviewedCourseNotificationMailsRequestDto} from '@/classes/dto/course-manager/request/SendReviewedCourseNotificationMails.request.dto';
import {MailDraftsPreviewResponseDto} from '@/classes/dto/_common/mail-review/MailDraft.dto';
import {RepairCourseLinkRequestDto} from '@/classes/dto/course-manager/request/RepairCourseLink.request.dto';
import {MailReportResponseDto} from '@/classes/dto/_common/response/mailReport.response.dto';
import {CourseDocument} from '@/documentTypes/course-manager/Course.document';
import {CreateLessonMoveRequestDto} from '@/classes/dto/course-manager/request/lessonMove/CreateLessonMove.request.dto';
import {UpdateLessonMoveRequestDto} from '@/classes/dto/course-manager/request/lessonMove/UpdateLessonMove.request.dto';
import {FindLessonMovesRequestDto} from '@/classes/dto/course-manager/request/lessonMove/FindLessonMoves.request.dto';
import {LessonMove} from '@/documentTypes/course-manager/LessonMove.document';
import {
    LessonMoveTeacherCheckRequestDto,
    LessonMoveTeacherCheckResponse,
} from '@/classes/dto/course-manager/request/lessonMove/LessonMoveTeacherCheck.request.dto';
import {
    LessonMoveTeacherImpactRequestDto,
    LessonMoveTeacherImpactResponse,
} from '@/classes/dto/course-manager/request/lessonMove/LessonMoveTeacherImpact.request.dto';
import {DecideLessonMoveTeacherRequestDto} from '@/classes/dto/course-manager/request/lessonMove/DecideLessonMoveTeacher.request.dto';
import {StaffingGapsResponse} from '@/classes/dto/course-manager/response/StaffingGaps.response.dto';

/** LessonMove as returned by the API (effectiveTeacher only with FindLessonMovesRequestDto.resolveEffective) */
export type LessonMoveResponse = LeanDocument<LessonMove> & { _id: Types.ObjectId; effectiveTeacher?: Types.ObjectId | null };

/** 409/422 of the lesson move endpoints carry a German message for the user: handled by the caller */
const LESSON_MOVE_STATUSES = {handledStatuses: [409, 422]};

/**
 * This class is used for communicating with the course manager microservice.
 * @class CourseManagerAPI
 * @author Joshua Seipel
 */
export class CourseManagerAPI extends API {
    static service = 'course-manager';

    static async create(doc: CreateCourseRequestDto): Promise<LeanDocument<CourseDocument>> {
        return this.post('', doc);
    }

    /** Review flow of the timetable mail: rendered drafts of one recipient group */
    static async previewCourseMails(data: PreviewCourseMailsRequestDto): Promise<MailDraftsPreviewResponseDto> {
        return this.post('course-mail/preview', data);
    }

    /** Review flow of the timetable mail: sends the (edited) drafts of one recipient group through the messenger */
    static async sendReviewedCourseMails(data: SendReviewedCourseMailsRequestDto): Promise<MailReportResponseDto> {
        return this.post('course-mail/send', data);
    }

    /** Review flow of the course change notification (after the change is saved): rendered drafts of one recipient group */
    static async previewCourseNotificationMails(id: Types.ObjectId, data: PreviewCourseNotificationMailsRequestDto): Promise<MailDraftsPreviewResponseDto> {
        return this.post(`${id}/notification-mail/preview`, data);
    }

    /** Review flow of the course change notification: sends the (edited) drafts of one recipient group through the messenger */
    static async sendReviewedCourseNotificationMails(id: Types.ObjectId, data: SendReviewedCourseNotificationMailsRequestDto): Promise<MailReportResponseDto> {
        return this.post(`${id}/notification-mail/send`, data);
    }

    static async find(data: FindCourseRequestDto): Promise<{
        courses: LeanDocument<CourseDocument>[];
        maxPage: number
    }> {
        return this.get(``, data);
    }

    /** Like find, but a 403 is left to the caller (no global "Unauthorized" dialog), e.g. for optional previews */
    static async findQuietly(data: FindCourseRequestDto): Promise<{
        courses: LeanDocument<CourseDocument>[];
        maxPage: number
    }> {
        return this.get(``, data, undefined, {handledStatuses: [403]});
    }

    static async findById(id: Types.ObjectId, options?: FindByIdOptionsRequestDto): Promise<LeanDocument<CourseDocument>> {
        return this.get(id.toString(), options);
    }

    static async addStudent(id: Types.ObjectId, update: AddStudentToCourseRequestDto): Promise<void> {
        return this.put(`addStudent/${id}`, update);
    }

    static async removeStudent(id: Types.ObjectId, update: RemoveStudentFromCourseRequestDto): Promise<void> {
        return this.put(`removeStudent/${id}`, update);
    }

    static async terminate(id: Types.ObjectId, update: TerminateCourseRequestDto): Promise<void> {
        return this.put(`terminate/${id}`, update);
    }

    static async repair(id: Types.ObjectId, update: RepairCourseLinkRequestDto): Promise<void> {
        return this.put(`repair/${id}`, update);
    }

    static async update(id: Types.ObjectId, update: UpdateCourseRequestDto): Promise<void> {
        return this.put(id.toString(), update);
    }

    static async remove(id: Types.ObjectId, options?: DeleteCourseRequestDto): Promise<void> {
        return this.delete(id.toString(), options);
    }

    /** "Stunde einmalig verschieben" into another week (or for courses with weekInterval > 1) */
    static async createLessonMove(body: CreateLessonMoveRequestDto): Promise<LessonMoveResponse> {
        return this.post('lesson-moves', body, undefined, LESSON_MOVE_STATUSES);
    }

    /** Changes a move; moving the lesson back to its original start without overrides deletes it ({deleted: true}) */
    static async updateLessonMove(id: Types.ObjectId | string, body: UpdateLessonMoveRequestDto): Promise<LessonMoveResponse | { deleted: true }> {
        return this.put(`lesson-moves/${id}`, body, LESSON_MOVE_STATUSES);
    }

    /** Undoes the move (the lesson takes place at its original date again) */
    static async deleteLessonMove(id: Types.ObjectId | string): Promise<void> {
        return this.delete(`lesson-moves/${id}`, undefined, LESSON_MOVE_STATUSES);
    }

    /** Move dialog: does the move cross a teacher change ("Wer hält die Stunde?"); 403/404 left to the caller */
    static async lessonMoveTeacherCheck(data: LessonMoveTeacherCheckRequestDto): Promise<LessonMoveTeacherCheckResponse> {
        return this.get('lesson-moves/teacher-check', data, undefined, {handledStatuses: [403, 404]});
    }

    /** Teacher change dialogs: moved lessons that cross the planned change and need a decision (403 left to the caller) */
    static async lessonMoveTeacherImpact(data: LessonMoveTeacherImpactRequestDto): Promise<LessonMoveTeacherImpactResponse> {
        return this.post('lesson-moves/teacher-impact', data, undefined, {handledStatuses: [403, 404]});
    }

    /** Decides who holds a (pending) moved lesson; 409 = original week already billed */
    static async decideLessonMoveTeacher(id: Types.ObjectId | string, body: DecideLessonMoveTeacherRequestDto): Promise<LessonMoveResponse> {
        return this.patch(`lesson-moves/${id}/teacher-decision`, body, LESSON_MOVE_STATUSES);
    }

    /** Moves whose original or moved lesson lies in the range (403 is left to the caller, e.g. for previews) */
    static async findLessonMoves(data: FindLessonMovesRequestDto): Promise<{ lessonMoves: LessonMoveResponse[] }> {
        return this.get('lesson-moves/find', data, undefined, {handledStatuses: [403]});
    }

    /** Dashboard: Kurse der laufenden Woche ohne Lehrkraft / ohne Raum (nur mit Lesezugriff auf alle Kurse) */
    static async staffingGapsWeek(): Promise<StaffingGapsResponse> {
        return this.get('staffing-gaps/week');
    }
}
